import { type TechFilterDefinition } from "~/features/selectionAssistant/gearbox/models";

import classes from "~/features/selectionAssistant/gearbox/components/DimensionsFilter/DimensionsFilter.module.less";
import { SingleDimensionFilter } from "~/features/selectionAssistant/gearbox/components/DimensionsFilter/components/SingleDimensionFilter";
import classNames from "classnames";
import { type DimensionKeys } from "~/features/selectionAssistant/gearbox/components/DimensionsFilter/types";
import { TECH_VALUE_CODE_LIST } from "~/features/selectionAssistant/gearbox/components/DimensionsFilter/constants";
import { useTranslation } from "react-i18next";
import { getTechFilterTranslations } from "~/features/selectionAssistant/gearbox/techFilterUtils";

interface Props {
  className?: string;
  definitions: Record<string, TechFilterDefinition>;
  values: Record<string, [number | undefined | null, number | undefined | null]>;
  onChange: (code: DimensionKeys, value: [number | undefined | null, number | undefined | null]) => void;
}

export const getDimensionFilterConfigs = ({
  definitions,
  onChange,
  values,
}: Pick<Props, "onChange" | "values" | "definitions">) => {
  const configs = TECH_VALUE_CODE_LIST.map((code) => definitions[code])
    .filter(Boolean)
    .map((definition) => {
      const code = definition.code as DimensionKeys;
      const { name, symbol, unit } = getTechFilterTranslations(code);
      return {
        code,
        label: (
          <>
            {name}{" "}
            <span className={classes.nobreak}>
              {symbol} ({unit})
            </span>
          </>
        ),
        min: definition.min,
        max: definition.max,
        value: values[code],
        onChange: (value: [number | undefined | null, number | undefined | null]) => onChange(code, value),
      };
    });

  const renderFilter = (config: (typeof configs)[0]) => <SingleDimensionFilter key={config.code} {...config} />;

  return {
    configs,
    renderFilter,
  };
};

export const DimensionsFilter = ({ className, definitions, onChange, values }: Props) => {
  const { t } = useTranslation();

  const { configs, renderFilter } = getDimensionFilterConfigs({ definitions, onChange, values });

  return (
    <div className={classNames(className, classes.root)}>
      <h2 className={classes.title}>{t("page.sa.gearbox.dimensions.title")}</h2>
      <div className={classes.filterList}>{configs.map(renderFilter)}</div>
    </div>
  );
};
